import partition from 'lodash/partition'
import { useLayoutEffect, useRef, useState } from 'react'
import {
  continueRender,
  delayRender,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion'
import { TranscriptionStyle, Word } from '../types'

export const Subtitles = ({
  words,
  transcriptionStyle: {
    highlightColor,
    highlightType,
    linesPerPage,
    highlightBgColor,
    scrollType,
    unPlayedColor,
    unPlayedOpacity,
    ...props
  },
}: {
  words: Word[]
  transcriptionStyle: TranscriptionStyle
}) => {
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const containerRef = useRef<HTMLDivElement>(null)
  const playedRef = useRef<HTMLDivElement>(null)
  const zoomRef = useRef<HTMLDivElement>(null)
  const [handle] = useState(() => delayRender())
  const [linesOffset, setLinesOffset] = useState(0)
  const lineHeight = Math.round(props.lineHeight * props.fontSize)

  useLayoutEffect(() => {
    if (!containerRef.current || !playedRef.current || !zoomRef.current) return

    const containerHeight = containerRef.current.getBoundingClientRect().height
    const playedHeight = playedRef.current.getBoundingClientRect().height
    const zoom = zoomRef.current.getBoundingClientRect().height / ZOOM_HEIGHT
    const linesTotal = Math.round(containerHeight / lineHeight / zoom)
    const linesPlayed = Math.round(playedHeight / lineHeight / zoom)
    const linesToOffset = Math.max(
      0,
      scrollType === 'line-by-line'
        ? Math.min(
            linesPlayed - Math.floor(linesPerPage / 2) - 1,
            linesTotal - linesPerPage,
          )
        : Math.floor((linesPlayed - 1) / linesPerPage) * linesPerPage,
    )

    setLinesOffset(linesToOffset)
    continueRender(handle)
  }, [frame, handle, lineHeight, linesPerPage, scrollType])

  const [played, unplayed] = partition(words, (s) => s.start * fps <= frame)
  const current = played.pop()

  return (
    <div
      className="relative overflow-y-clip"
      style={{ height: `${lineHeight * linesPerPage}px` }}
    >
      {!!props.textStrokeWidth && (
        <div
          className="absolute w-full"
          style={{
            ...props,
            lineHeight: `${lineHeight}px`,
            transform: `translateY(-${linesOffset * lineHeight}px)`,
          }}
        >
          <span
            style={{
              WebkitTextStrokeColor: props.textStrokeColor,
              WebkitTextStrokeWidth: props.textStrokeWidth,
            }}
          >
            {words.map((word) => `${word.text} `)}
          </span>
        </div>
      )}

      <div
        ref={containerRef}
        style={{
          ...props,
          lineHeight: `${lineHeight}px`,
          transform: `translateY(-${linesOffset * lineHeight}px)`,
        }}
      >
        <span ref={playedRef}>
          <span
            style={{
              color: highlightType === 'all-words' ? highlightColor : undefined,
            }}
          >
            {played.map((word) => `${word.text} `)}
          </span>

          {current && (
            <span
              style={{
                color:
                  highlightType !== 'all-words' ? highlightColor : undefined,
                ...(highlightType === 'last-word-bg' && {
                  background: highlightBgColor,
                  padding: props.fontSize / 10,
                  margin: -props.fontSize / 10,
                }),
              }}
            >
              {current.text}
            </span>
          )}
        </span>

        <span
          style={{
            color: unPlayedColor,
            opacity: unPlayedOpacity || 1,
          }}
        >
          {unplayed.map((word) => ` ${word.text}`)}
        </span>
      </div>

      <div
        className="absolute w-[1px]"
        ref={zoomRef}
        style={{ height: ZOOM_HEIGHT }}
      />
    </div>
  )
}

// Taller reference zoom div means better height calculation precision.
const ZOOM_HEIGHT = 100
